import React, { ReactNode } from "react";
import { Button, InfoContainer, Text } from "@lmn/design-system";
import styled from "styled-components";
import IconCircleInfo from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-linear/IconCircleInfo.js";
import { IconVoucher } from "@/Icons/IconVoucher";
import { SpecialDealDetail } from "@/lib/Types/SpecialDealDetail";
import { OfferRoomDetail } from "@/lib/Types/OfferRoomDetail";
import { OfferStatusTypes } from "@/lib/Types/DateListResponse/QuickVacancy";
import DialogTooltip from "@/components/UI/Tooltip/DialogTooltip";
import MobileAccordion from "@/components/UI/Accordion/MobileAccordion";
import PaymentTypes from "./PaymentTypes";
import TravellerPriceList from "@/components/UI/TravellerPriceList/TravellerPriceList";
import { formatPriceWithDecimals } from "@/lib/Helpers/formatPrice";

const Container = styled.div(() => ({
  gridArea: "price",
  display: "flex",
  flexDirection: "column" as const,
  gap: "0.5rem",
}));

const PriceInfoContainer = styled.div(() => ({
  display: "flex",
  flexDirection: "column" as const,
  gap: "0.5rem",
}));

const SpecialDealBadge = styled.div(({ theme }) => ({
  padding: "4px 8px",
  backgroundColor:
    theme.kallisto.color.background.static.backgroundSuccessContrast,
  color: theme.kallisto.color.text.static.textOnDark,
  borderRadius: "4px",
  border: `1px solid ${theme.kallisto.color.background.static.backgroundSuccessContrast}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "fit-content",
  gap: "0.5rem",
  marginBottom: "0.5rem",

  span: {
    color: theme.kallisto.color.text.static.textOnDark,
    lineHeight: "normal",
  },
}));

const PriceRow = styled.div(() => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-end",
  gap: "1rem",
}));

const PriceSection = styled.div(() => ({
  display: "flex",
  flexDirection: "column" as const,
}));

const PriceInline = styled.div(() => ({
  display: "flex",
  alignItems: "baseline",
  gap: "0.5rem",
  flexWrap: "wrap" as const,
}));

const MainPrice = styled(Text)(({ theme }) => ({
  color: theme.kallisto.color.text.static.textPrimary,
  whiteSpace: "nowrap",
}));

const StrikedPrice = styled.div(({ theme }) => ({
  color: theme.kallisto.color.text.static.textSuccess,
  display: "flex",
  alignItems: "center",
  gap: "0.2rem",
  cursor: "pointer",
  p: {
    textDecoration: "line-through",
    color: theme.kallisto.color.text.static.textSuccess,
  },
  svg: {
    marginBottom: "2px",
  },
}));

interface TourOperator {
  code?: string;
}

interface OfferPriceProps {
  price: number;
  estimatedTotalPrice: number | null;
  confirmedTotalPrice: number | null;
  specialDealsDetails: SpecialDealDetail[] | null | undefined;
  travellers: number;
  isChecked: boolean;
  isAvailable: boolean;
  isChecking: boolean;
  onCheckAvailability: () => void;
  onBookNow: () => void;
  paymentTypes?: string[];
  tourOperator?: TourOperator | null;
  offerRoomDetails?: OfferRoomDetail[] | null;
  showPriceList?: boolean;
  statusType?: OfferStatusTypes;
  toVoucherInvalidated?: boolean;
  isBookingLoading?: boolean;
  lastminuteHotelDetailUrl?: string;
}

const ExternalCtaLink = styled.a({
  textDecoration: "none",
});

const getVacancyStatusMessage = (
  statusType?: OfferStatusTypes
): ReactNode | null => {
  switch (statusType) {
    case OfferStatusTypes.ON_REQUEST:
      return "Auf Anfrage buchbar.";
    case OfferStatusTypes.NOT_AVAILABLE:
      return "Leider ausgebucht.";
    case OfferStatusTypes.XHR_ERROR:
      return "Veranstalter antwortet nicht.";
    case OfferStatusTypes.GDS_COMMUNICATION_ERROR:
    case OfferStatusTypes.INVALID_FLIGHT_DATA:
    case OfferStatusTypes.NO_VALID_PAYMENT_METHOD:
    case OfferStatusTypes.ERROR:
      return "Leider nicht mehr verfügbar.";
    case OfferStatusTypes.TOUROPERATOR_COMMUNICATION_ERROR:
      return (
        <>
          Buchung möglich.
          <br />
          Preis konnte nicht online überprüft werden.
        </>
      );
    default:
      return null;
  }
};

const OfferPrice: React.FC<OfferPriceProps> = ({
  price,
  estimatedTotalPrice,
  confirmedTotalPrice,
  specialDealsDetails,
  travellers,
  isChecked,
  isAvailable,
  isChecking,
  onCheckAvailability,
  onBookNow,
  paymentTypes,
  tourOperator,
  offerRoomDetails,
  showPriceList,
  statusType,
  toVoucherInvalidated,
  isBookingLoading,
  lastminuteHotelDetailUrl,
}) => {
  // Filter out BOOK_ON_OPTION deals (shown elsewhere)
  const filteredDeals =
    specialDealsDetails?.filter(
      (deal) => deal?.specialDealTypes !== "BOOK_ON_OPTION"
    ) ?? [];

  // Calculate strike-through price (original price before discount)
  const strikeThroughPrice =
    filteredDeals.length > 0 && filteredDeals[0].totalPriceBeforeSpecialDeal
      ? filteredDeals[0].totalPriceBeforeSpecialDeal
      : null;

  // Calculate display price - prioritize confirmed price after QuickVacancy
  const displayPrice =
    confirmedTotalPrice ??
    (filteredDeals.length > 0 && filteredDeals[0].totalPriceAfterSpecialDeal
      ? filteredDeals[0].totalPriceAfterSpecialDeal
      : null) ??
    estimatedTotalPrice ??
    price * travellers;

  // Calculate price per person
  const pricePerPerson =
    displayPrice && travellers > 0 ? displayPrice / travellers : price;

  // Has discount
  const hasDiscount =
    !!strikeThroughPrice &&
    displayPrice !== null &&
    strikeThroughPrice > displayPrice;

  // Get deal label for badge
  const dealLabel = filteredDeals.length > 0 ? filteredDeals[0].label : null;

  // Calculate price difference after QuickVacancy check
  const priceDifference =
    isChecked && confirmedTotalPrice && estimatedTotalPrice
      ? confirmedTotalPrice - estimatedTotalPrice
      : null;

  // Get price difference message (only if +-5 or more)
  const getPriceDifferenceMessage = (): string | null => {
    // Don't show price difference for TO_VOUCHER deals
    const hasToVoucherDeal = filteredDeals.some(
      (deal) => deal.specialDealTypes === "TO_VOUCHER"
    );
    if (hasToVoucherDeal) return null;

    if (!priceDifference) return null;
    const formatted = Math.abs(priceDifference).toFixed(0);
    if (priceDifference <= -5) {
      return `Ihre Reise ist um ${formatted}€ günstiger geworden!`;
    } else if (priceDifference >= 5) {
      return `Der Veranstalter meldet eine Preiserhöhung um ${formatted}€`;
    }
    return null;
  };

  const priceDiffMessage = getPriceDifferenceMessage();

  // Build combined vacancy feedback messages
  const availabilityMessage = getVacancyStatusMessage(statusType);
  const toVoucherMessage = toVoucherInvalidated
    ? "Leider ist der Aktionsrabatt für dieses Angebot nicht gültig."
    : null;

  const feedbackMessages = [
    availabilityMessage,
    priceDiffMessage,
    toVoucherMessage,
  ].filter(Boolean);

  return (
    <Container>
      {isChecked && (
        <MobileAccordion
          trigger={<Text variant="BodySmall">Preis- und Zahlungsdetails</Text>}
        >
          <PriceInfoContainer>
            {/* Payment types */}
            {paymentTypes && paymentTypes.length > 0 && (
              <PaymentTypes
                paymentTypes={paymentTypes}
                tourOperator={tourOperator}
              />
            )}

            {/* Vacancy feedback messages (after check) */}
            {isAvailable && feedbackMessages.length > 0 && (
              <InfoContainer
                icon={IconCircleInfo}
                type="general"
                message={feedbackMessages.map((msg, i) => (
                  <span key={i}>{msg}</span>
                ))}
                slim
              />
            )}

            {/* Traveller price list (after availability check) */}
            {showPriceList &&
              offerRoomDetails &&
              offerRoomDetails.length > 0 && (
                <TravellerPriceList offerRoomDetails={offerRoomDetails} />
              )}
          </PriceInfoContainer>
        </MobileAccordion>
      )}

      <div style={{ marginTop: "auto" }}>
        {/* Row 1: Special Deal Badge */}
        {dealLabel && (
          <SpecialDealBadge>
            <IconVoucher size={18} aria-hidden="true" />
            <Text variant="BodySmall" forceTag="span">
              {dealLabel}
            </Text>
          </SpecialDealBadge>
        )}
        {/* Row 2: Price + Button */}
        <PriceRow>
          <PriceSection>
            {/* Main price line */}
            <PriceInline data-testid="offer-price">
              <MainPrice variant="Title1" forceTag="span">
                {formatPriceWithDecimals(displayPrice) || "0"}
              </MainPrice>

              {/* Strike-through price with tooltip */}
              {hasDiscount && strikeThroughPrice && (
                <DialogTooltip
                  title="Special Deal"
                  content={
                    <Text variant="BodySmall">
                      Der angegebene Preisvorteil basiert auf einem Vergleich
                      des aktuellen Preises mit dem höchsten vom Kunden bei uns
                      geprüften und verfügbaren Angebot für die gleiche Reise in
                      einem Zeitraum von jeweils 15 Tagen vor und nach Ihrem
                      Anreisedatum. Um fair vergleichen zu können, werden alle
                      vorliegenden Details der Angebote berücksichtigt (Hotel,
                      Abflughafen, Reisedauer, Verpflegung, Zimmertyp). Das
                      bedeutet, dass Sie diese Reise jetzt zu einem günstigeren
                      Preis erhalten als zu bisher gesehenen Preisen im selben
                      Zeitraum.
                    </Text>
                  }
                  trigger={
                    <StrikedPrice>
                      <Text variant="BodySmall" weight="regular">
                        {strikeThroughPrice.toFixed(0)} €
                      </Text>
                      <IconCircleInfo size={14} />
                    </StrikedPrice>
                  }
                />
              )}
            </PriceInline>

            {/* Price per person */}
            {travellers > 1 && pricePerPerson && !isChecked && (
              <Text variant="BodySmall" forceTag="span" color="textSecondary">
                {formatPriceWithDecimals(pricePerPerson)} p.P.
              </Text>
            )}
          </PriceSection>

          {/* Button */}
          {lastminuteHotelDetailUrl ? (
            <ExternalCtaLink
              href={lastminuteHotelDetailUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="external-cta-link"
            >
              <Button variant="secondary">
                <span style={{ whiteSpace: "nowrap" }}>Zum Angebot</span>
              </Button>
            </ExternalCtaLink>
          ) : (
            <>
              {!isChecked && (
                <Button
                  appearance="outlined"
                  variant="secondary"
                  onClick={onCheckAvailability}
                  loading={isChecking}
                  data-testid="check-availability-button"
                >
                  <span style={{ whiteSpace: "nowrap" }}> Angebot prüfen</span>
                </Button>
              )}
              {isChecked && (
                <Button
                  variant="secondary"
                  disabled={!isAvailable}
                  onClick={onBookNow}
                  loading={isBookingLoading}
                  data-testid="book-now-button"
                >
                  {isAvailable ? "Zur Buchung" : "Leider ausgebucht"}
                </Button>
              )}
            </>
          )}
        </PriceRow>
      </div>
    </Container>
  );
};

export default OfferPrice;
